<template>
  <div
    class="bm-order"
    :class="{ 'bm-order--held': order.held }"
    :data-testid="`order-${order.id}`"
    :data-order-index="index"
  >
    <div class="bm-order__who">
      <div class="bm-order__shopper">{{ order.shopper }}</div>
      <!--
        The shopper's address, as a real mailto. Jake, 2026-09-22: the
        distributor needs the name AND the email on the order, because the
        pre-release window exists for fixing orders and fixing one often
        means asking the person who placed it.

        Rendered only when there IS one -- checkout_fields is nullable and
        unconstrained, so an order with no address is a real state, and an
        empty `mailto:` is worse than showing nothing.
      -->
      <a
        v-if="order.shopperEmail"
        class="bm-order__email"
        :href="`mailto:${order.shopperEmail}`"
        data-testid="order-shopper-email"
        >{{ order.shopperEmail }}</a
      >
      <div class="bm-order__meta">
        {{ order.dept }} &middot;
        <span :data-testid="`order-label-${order.id}`">{{ order.orderLabel || order.id }}</span>
      </div>
      <div v-if="showDecorator && order.decoratorName" class="bm-order__meta">
        Decorator:
        <span :data-testid="`order-decorator-${order.id}`">{{ order.decoratorName }}</span>
      </div>
      <!--
        The decorator cancelled this order after it was sent (Jake: owners
        see it; buyers never do). It is NOT refunded automatically, so the
        badge is what tells the distributor there is something to resolve.
      -->
      <div
        v-if="order.decoratorCancelled"
        class="bm-order__decorator-cancelled"
        :data-testid="`order-decorator-cancelled-${order.id}`"
      >Decorator cancelled</div>
    </div>

    <div class="bm-order__what">
      {{ summary }}
      <!--
        WHICH line matched the filter. Jake asked for both halves: "Filter
        orders AND highlight matching line items." On a nine-style order,
        knowing the order matched does not tell you where to look.

        Only style matches highlight. A filter that matched the shopper's
        name says nothing about any particular line, and highlighting one
        anyway would point at something that is not the reason the order is
        on screen.
      -->
      <div v-if="matchedLines.length" class="bm-order__matches">
        <span
          v-for="l in matchedLines"
          :key="l.id"
          class="bm-order__match"
          :data-testid="`order-line-match-${l.id}`"
        >
          {{ [l.style, l.color, l.size].filter(Boolean).join(" &middot; ") }}
        </span>
      </div>
      <div v-if="order.held" class="bm-order__heldnote">
        <i class="material-icons bm-order__heldicon" aria-hidden="true">credit_card_off</i>
        <span>
          <b>Held back &mdash; {{ order.reason }}.</b>
          <template v-if="order.movedTo"> Moved to batch {{ order.movedTo }}, which is still open.</template>
        </span>
      </div>
    </div>

    <div class="bm-order__count">
      {{ items }} {{ items === 1 ? 'item' : 'items' }}<br />
      <span
        v-if="order.placed"
        class="bm-order__placed"
        :data-testid="`order-placed-${order.id}`"
      >Placed {{ order.placed }}</span>
    </div>

    <div class="bm-order__total" :data-testid="`order-total-${order.id}`">
      {{ money(total) }}
    </div>

    <div class="bm-order__actions" :data-testid="`order-actions-${order.id}`">
      <!--
        RELEASED IS INERT. On a non-editable batch there are no action
        buttons at all -- not disabled ones, absent ones. What replaces them
        is a plain "Locked" statement, so the state is legible without
        implying an action exists.
      -->
      <template v-if="editable">
        <!--
          The id-keyed testid is what component specs already bind. The
          POSITIONAL alias on the wrapper exists because a ui-runner
          scenario cannot use the id-keyed one: selectors are exact
          `data-testid=` matches with no globbing, and a seeded order's
          display id is not knowable when a scenario is authored
          (BatchSeeder reuses whichever recent orders exist). Clicking the
          wrapper reaches the button inside it.
        -->
        <span
          class="bm-order__editwrap"
          :data-testid="index != null ? `order-edit-${index}` : undefined"
        >
          <button
            type="button"
            class="bm-btn bm-btn--sm"
            :data-testid="`edit-${order.id}`"
            @click="$emit('edit', order)"
          >
            <i class="material-icons bm-btn__icon" aria-hidden="true">edit</i>
            Edit
          </button>
        </span>
        <!--
          Move to another unreleased batch of this store. Offered only when
          the panel says there IS another editable batch to move to, and
          never on a held-back order: that one is card recovery's to move
          (the API refuses it as `order_not_movable`).
        -->
        <button
          v-if="movable && !order.held"
          type="button"
          class="bm-btn bm-btn--sm"
          :data-testid="`move-${order.id}`"
          @click="$emit('move', order)"
        >
          <i class="material-icons bm-btn__icon" aria-hidden="true">drive_file_move</i>
          Move
        </button>
        <button
          v-if="order.canCancel !== false"
          type="button"
          class="bm-btn bm-btn--sm bm-btn--dangerquiet"
          :data-testid="`cancel-${order.id}`"
          @click="$emit('cancel', order)"
        >
          Cancel
        </button>
      </template>
      <span v-else class="bm-order__locked" data-testid="order-locked">
        <i class="material-icons bm-order__lockicon" aria-hidden="true">lock</i>Locked
      </span>
    </div>
  </div>
</template>

<script>
/**
 * One order inside an expanded batch.
 *
 * The `editable` decision belongs to the BATCH, not the order: orders are
 * editable exactly while their batch is Open or Holding. Passing it in keeps
 * a single source of that truth rather than each row re-deriving it.
 */
import {
  money,
  orderTotal,
  itemCount,
  lineMatchesFilter,
} from "@/services/batchManagementService";

export default {
  name: "BatchOrderRow",
  props: {
    order: { type: Object, required: true },
    editable: { type: Boolean, default: false },
    // Position in the rendered list, for a testid a ui-runner scenario can
    // actually bind (see the comment on the edit wrapper). Optional so
    // existing callers that do not pass it keep working -- the positional
    // testids are simply absent then.
    index: { type: Number, default: null },
    // The live type-down term, so the row can say WHICH of its lines matched.
    // Defaults to "" -- an unfiltered row highlights nothing.
    filter: { type: String, default: "" },
    // The batch went to several decorators, so this row names its own.
    showDecorator: { type: Boolean, default: false },
    // Another editable batch of this store exists, so the order can be moved
    // into it. Decided by the panel, which is the only component that sees
    // every batch.
    movable: { type: Boolean, default: false },
  },
  emits: ["edit", "cancel", "move"],
  computed: {
    /**
     * The lines that matched the filter ON A STYLE.
     *
     * Deliberately NOT "every line, when the order matched": an order that
     * matched on the shopper's name tells you nothing about any particular
     * line, and marking one would assert a reason that is not the reason.
     * Uses the SAME predicate the list filters with, so the highlight and
     * the inclusion cannot disagree.
     */
    matchedLines() {
      if (!this.filter) return [];
      return (this.order.lines || []).filter((l) =>
        lineMatchesFilter(l, this.filter)
      );
    },
    total() {
      return orderTotal(this.order);
    },
    items() {
      return itemCount(this.order);
    },
    /**
     * What was ordered, in one line.
     *
     * A missing colour or size is a REAL condition, not a seeding accident:
     * `batchManagementService` passes `color` / `size` through untouched, and
     * an order item whose product carries no parseable attributes arrives with
     * both null. Rendering the absence is what made batch B-7 look broken --
     * `null . null` on single-line orders, `9 styles . , , , , , , ,` on
     * multi-line ones. So every part here is filtered before it is joined, and
     * the separator travels WITH its part rather than being laid down between
     * fixed slots. When nothing survives the filter we say what we do know --
     * the item count -- because a blank row reads as a load failure.
     */
    summary() {
      const lines = this.order.lines || [];
      const present = (v) => {
        const s = v == null ? "" : String(v).trim();
        return s.length ? s : null;
      };
      const itemsLabel = `${this.items} ${this.items === 1 ? "item" : "items"}`;

      if (lines.length === 1) {
        const l = lines[0];
        const style = present(l.style);
        const parts = [
          style ? style.split(" ").slice(-3).join(" ") : null,
          present(l.color),
          present(l.size),
        ].filter(Boolean);
        return parts.length ? parts.join(" · ") : itemsLabel;
      }

      // Repeating "M" eight times is noise, not information; the distinct set
      // is what a reviewer is actually scanning for.
      const sizes = [...new Set(lines.map((l) => present(l.size)).filter(Boolean))];
      const head = `${lines.length} styles`;
      return sizes.length ? `${head} · ${sizes.join(", ")}` : head;
    },
  },
  methods: { money },
};
</script>

<style scoped>
.bm-order {
  display: grid;
  /*
    The total and actions tracks are FIXED, shared by every row (card #604).
    Each row is its own grid, so a content-sized track would put the total at
    a different x on every row depending on which buttons that row renders
    (Edit + Move + Cancel / Edit + Cancel / Locked). 256px holds the widest
    set (Edit + Move + Cancel, ~235px) and the buttons wrap inside it if a
    font runs wide; the 104px total holds "$99,999.99". The text columns are
    minmax(0, fr) so THEY shrink first. The old 190px actions track did not
    fit Edit + Move + Cancel, which is what covered the total.
  */
  grid-template-columns: minmax(0, 1.5fr) minmax(0, 1.6fr) minmax(0, 1fr) 104px 256px;
  gap: 14px;
  align-items: center;
  padding: 11px 20px;
  border-top: 1px solid var(--tf-border);
  background: var(--tf-surface);
}

.bm-order--held {
  background: var(--tf-error-soft);
}

.bm-order__who {
  min-width: 0;
}

.bm-order__shopper {
  font-size: 14px;
  color: var(--tf-fg-1);
}

.bm-order__email {
  display: block;
  font-size: 12px;
  color: var(--tf-primary);
  margin-top: 1px;
  text-decoration: none;
  overflow-wrap: anywhere;
}

.bm-order__email:hover {
  text-decoration: underline;
}

.bm-order__meta {
  font-size: 11.5px;
  color: var(--tf-fg-quiet);
  margin-top: 1px;
}

.bm-order__matches {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 4px;
}

/*
  A highlighted line reads as FOUND, not as an error or a warning. The soft
  primary tint is the same one the swatch and logo selections use, so
  "this is the thing you asked for" looks consistent across the surface.
*/
.bm-order__match {
  font-size: 11.5px;
  padding: 2px 7px;
  border-radius: var(--tf-radius-pill, 9999px);
  background: var(--tf-primary-soft);
  color: var(--tf-primary-dark, var(--tf-primary));
}

.bm-order__what {
  font-size: 12.5px;
  color: var(--tf-fg-3);
  min-width: 0;
}

.bm-order__decorator-cancelled {
  display: inline-block;
  margin-top: 4px;
  font-size: 11.5px;
  font-weight: 600;
  color: var(--tf-error-dark);
}

.bm-order__heldnote {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  margin-top: 4px;
  font-size: 12px;
  color: var(--tf-error-dark);
  line-height: 1.45;
}

.bm-order__heldnote b {
  font-weight: 500;
}

.bm-order__heldicon {
  font-size: 14px;
  margin-top: 1px;
  flex-shrink: 0;
}

.bm-order__count {
  font-size: 12.5px;
  color: var(--tf-fg-quiet);
}

.bm-order__placed {
  font-size: 11.5px;
}

.bm-order__total {
  font-size: 14.5px;
  font-weight: 500;
  text-align: right;
  white-space: nowrap;
}

.bm-order__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: flex-end;
}

.bm-order__locked {
  font-size: 11.5px;
  color: var(--tf-fg-quiet);
  display: inline-flex;
  align-items: center;
  gap: 5px;
}

.bm-order__lockicon {
  font-size: 13px;
}

.bm-btn {
  border-radius: 9999px;
  font-family: inherit;
  font-weight: 500;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border: 1px solid var(--tf-border-strong);
  background: var(--tf-surface);
  color: var(--tf-fg-2);
  min-height: 36px;
  padding: 6px 14px;
  font-size: 12.5px;
}

.bm-btn--dangerquiet {
  background: transparent;
  border-color: transparent;
  color: var(--tf-error-dark);
}

.bm-btn__icon {
  font-size: 15px;
}

/*
  Narrow ORDER LIST (not viewport: the store editor has a side nav, so the
  list is much narrower than the window). `.bm-detail__orders` in BatchRow is
  the container. Below this the five-column row cannot give the text columns
  room, so the actions drop to their own full-width row under the total.
*/
@container bm-orders (max-width: 760px) {
  .bm-order {
    grid-template-columns: minmax(0, 1fr) 104px;
  }

  .bm-order__actions {
    grid-column: 1 / -1;
    justify-content: flex-start;
  }
}

/* Mobile: one column, 44px targets, actions stack under the summary. */
@media (max-width: 599px) {
  .bm-order {
    display: block;
    padding: 13px 16px;
  }

  .bm-order__shopper {
    font-size: 14.5px;
    font-weight: 500;
  }

  .bm-order__count,
  .bm-order__what {
    margin-top: 3px;
  }

  .bm-order__total {
    text-align: left;
    font-size: 15px;
    margin-top: 4px;
  }

  .bm-order__actions {
    justify-content: flex-start;
    margin-top: 11px;
  }

  .bm-btn {
    min-height: 44px;
  }
}
</style>
